import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { t } from '@vk-hr-tek/core/translations/t';
import { TypographyColor } from '@vk-hr-tek/ui/Typography';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip';

import {
  UserGroup,
  ManagerSubstitutesEventType,
  UkepCertificate,
  Attorney,
  ManageableCompanyItem,
  ManagerSubstitute,
} from '@app/gen/users';
import { EmployeeManager, UserProfileEmployee } from '@app/gen/user';
import { ProfileItem, DateRange } from '@app/gen/settings';
import {
  AttorneyType,
  AttorneyStatus,
  AttorneyReadableStatus,
} from '@app/types';
import { ATTORNEYS_POWERS_DEFAULT } from '@app/constants';

import {
  CompanyPositionSubstitutes,
  ExtendedUser,
  SmevErrorCode,
} from '../slice/user.state';
import {
  SubstituteType,
  EmployeeManagerWithGroupedTypes,
  ProfileEmployee,
  PhoneNumber,
} from '../types';
import { EMPLOYEE_MANAGERS_PRIORITY } from '../constants';

@injectable()
export class UserMapper {
  constructor(private format: FormatService) {}

  process(user: ExtendedUser) {
    return {
      id: user.id,
      name: `${user.last_name} ${user.first_name} ${user.middle_name}`,
      nameWithInitials: `${user.last_name} ${user.first_name[0]}.${user.middle_name[0]}.`,
      firstName: user.first_name,
      lastName: user.last_name,
      birthday: user?.birth_date,
      phSearchEnabled: user.ph_search_enabled,
      avatar: `${user.last_name[0]}${user.first_name[0]}`.toUpperCase(),
      snils: user.snils ? this.format.toSnils(user.snils) : undefined,
      phone: user.phone ? this.format.toPhone(user.phone) : undefined,
      certificate: {
        exists: user.certificate.exists,
        type: user.certificate.sign_type === 'ukep' ? 'УКЭП' : 'УНЭП',
        disabled: user.certificate.sign_type === 'disabled',
        description:
          user.certificate.sign_type === 'ukep'
            ? 'УКЭП (Усиленная квалифицированная электронная подпись)'
            : 'УНЭП (Усиленная неквалифицированная электронная подпись)',
        activity:
          user.certificate.valid_from && user.certificate.valid_to
            ? `Действителен: с ${this.format.toDate(
                user.certificate.valid_from,
              )} по ${this.format.toDate(user.certificate.valid_to)}`
            : 'У вас нет действующей электронной подписи этого типа',
        smevErrors: user.certificate.errors || [],
      },
      notifications: {
        telegram: user.telegram_enabled,
      },
      groups: this.processGroups(user.groups, user.attorneys),
      employees: user.employees.map((employee) => ({
        ...employee,
        dismissed_at: employee.dismissed_at
          ? this.format.toDate(employee.dismissed_at)
          : '',
        isDismissed:
          !!employee.dismissed_at &&
          !Number.isNaN(Date.parse(employee.dismissed_at)) &&
          new Date(employee.dismissed_at) <= new Date(),
        editSubstitutes: employee.permissions.edit_substitutes,
        vacation_days: employee.vacation_days,
        groups: this.processGroups(user.groups, user.attorneys).reduce(
          (
            acc: {
              id: string;
              name: string;
              roles: {
                id: string;
                name: string;
                profiles?: ProfileItem[];
              }[];
              attorneys: AttorneyType[];
            },
            group,
          ) => {
            if (group.id === employee.company.id) {
              acc = {
                ...acc,
                ...group,
                attorneys: acc.attorneys
                  ? [...acc.attorneys, ...group.attorneys]
                  : [...group.attorneys],
                roles: acc.roles
                  ? [...acc.roles, ...group.roles]
                  : [...group.roles],
              };
            }
            return acc;
          },
          { id: '', name: '', attorneys: [], roles: [] },
        ),
      })),
      has_company_side: user.has_company_side,
      permissions: {
        editSubstitutes: user.employees.some(
          (employee) => employee.permissions.edit_substitutes,
        ),
      },
      ukepCertificates: this.processUkepCertificates(user?.ukep_certificates),
      attorneys: user.attorneys.map((attorney) =>
        this.processAttorney(attorney),
      ),
    };
  }

  processProfileEmployees(employees: UserProfileEmployee[]): ProfileEmployee[] {
    return employees.map((employee) => ({
      ...employee,
      directManager: employee.direct_manager,
      managers: this.processEmployeeManagers(employee.managers),
      dismissed_at: employee.dismissed_at
        ? this.format.toDate(employee.dismissed_at)
        : '',
    }));
  }

  processUkepCertificates(certificates: UkepCertificate[] | undefined) {
    if (!certificates) return;

    const uniqueCertificates = new Map();

    certificates.forEach((certificate) => {
      if (uniqueCertificates.has(certificate.serial_number)) {
        const current = uniqueCertificates.get(certificate.serial_number);
        uniqueCertificates.set(certificate.serial_number, {
          ...current,
          company_name: `${current.company_name}, ${certificate.company_name}`,
        });
      } else {
        uniqueCertificates.set(certificate.serial_number, certificate);
      }
    });

    return Array.from(uniqueCertificates.values()).map((certificate) => ({
      activity:
        certificate.valid_from && certificate.valid_to
          ? `Действителен: с ${this.format.toDate(
              certificate.valid_from,
            )} по ${this.format.toDate(certificate.valid_to)}`
          : '',
      companyId: certificate.company_id,
      companyName: `Загружен для: ${certificate.company_name}`,
      description: 'УКЭП (Усиленная квалифицированная электронная подпись)',
      issuedBy: `Выдан: ${certificate.issued_by}`,
      owner: `Владелец: ${certificate.owner}`,
      serialNumber: `Сертификат: ${certificate.serial_number}`,
      type: 'УКЭП',
    }));
  }

  processAttorneyStatus(
    status: AttorneyStatus,
    isDefault: boolean,
  ): AttorneyReadableStatus {
    if (status === 'new') {
      return 'На подписании';
    }

    if (status === 'expired') {
      return 'Истекла';
    }

    if (status === 'revoked') {
      return 'Отозвана';
    }

    if (status === 'canceled') {
      return 'Отключена';
    }

    if (isDefault && status === 'signed') {
      return 'Активна';
    }

    if (isDefault && status === 'revoking') {
      return 'В процессе отзыва';
    }

    return 'Действующая';
  }

  processAttorneyStatusToTypographyColor(
    attorneyStatus: AttorneyStatus,
    attorneyIsDefault: boolean,
  ): TypographyColor {
    if (attorneyStatus === 'new') {
      return 'accent.text.warning';
    }

    if (attorneyIsDefault && attorneyStatus === 'signed') {
      return 'accent.text.success';
    }

    if (attorneyIsDefault && attorneyStatus === 'revoking') {
      return 'accent.text.warning';
    }

    return 'text.light.tertirary';
  }

  processAttorneyStatusToChipColor(
    attorneyStatus: AttorneyStatus,
  ): ChipColorUnion {
    if (attorneyStatus === 'new') {
      return 'blue';
    }

    if (attorneyStatus === 'signed') {
      return 'green';
    }

    if (attorneyStatus === 'revoking') {
      return 'orange';
    }

    if (attorneyStatus === 'revoked') {
      return 'red';
    }

    if (attorneyStatus == 'canceled') {
      return 'purple';
    }

    return 'gray';
  }

  processAttorney(attorney: Attorney): AttorneyType {
    return {
      id: attorney.id,
      UID: attorney.id,
      status: attorney.status,
      readableStatus: this.processAttorneyStatus(
        attorney.status,
        attorney.is_default,
      ),
      snils: this.format.toSnils(attorney.snils),
      issuer: attorney.issuer,
      isDefault: attorney.is_default,
      creationDate: this.format.toDate(attorney.creation_date),
      validFrom: this.format.toDate(attorney.valid_from),
      validTo: this.format.toDate(attorney.valid_to),
      companyId: attorney.company.id,
      companyName: attorney.company.name,
      powers: attorney.powers || ATTORNEYS_POWERS_DEFAULT,
      revocationReason: attorney.revocation_reason || '',
      imported: attorney.imported,
      fullName: attorney.full_name,
      typographyColor: this.processAttorneyStatusToTypographyColor(
        attorney.status,
        attorney.is_default,
      ),
      chipColor: this.processAttorneyStatusToChipColor(attorney.status),
    };
  }

  processGroups(groups: UserGroup[], attorneys: Attorney[]) {
    const companies: Record<
      string,
      {
        id: string;
        name: string;
        roles: { id: string; name: string; profiles?: ProfileItem[] }[];
        attorneys: AttorneyType[];
      }
    > = {};

    groups.forEach((group) => {
      if (group.company.id in companies) {
        companies[group.company.id].roles.push({
          id: group.id,
          name: group.name,
          profiles: group.profiles,
        });
      } else {
        companies[group.company.id] = {
          id: group.company.id,
          name: group.company.name,
          roles: [{ id: group.id, name: group.name, profiles: group.profiles }],
          attorneys: attorneys
            .filter(
              (attorney) =>
                attorney.company.id === group.company.id &&
                (attorney.status === 'new' ||
                  attorney.status === 'signed' ||
                  attorney.status === 'revoking'),
            )
            .map((attorney) => this.processAttorney(attorney)),
        };
      }
    });

    return Object.keys(companies).map((id) => companies[id]);
  }

  processEmployeeManagersSort(
    currentManager: EmployeeManagerWithGroupedTypes,
    nextManager: EmployeeManagerWithGroupedTypes,
  ) {
    if (nextManager.types.length !== currentManager.types.length) {
      return nextManager.types.length - currentManager.types.length;
    }

    if (currentManager.types.length === 1 && nextManager.types.length === 1) {
      return (
        EMPLOYEE_MANAGERS_PRIORITY[currentManager.types[0]] -
        EMPLOYEE_MANAGERS_PRIORITY[nextManager.types[0]]
      );
    }

    return 0;
  }

  processEmployeeManagers(
    managers?: EmployeeManager[],
  ): EmployeeManagerWithGroupedTypes[] {
    if (!managers) {
      return [];
    }

    const groupedEmployeeManagers: Record<
      string,
      Omit<EmployeeManager, 'id'> & {
        types: Set<EmployeeManager['type']>;
        phones: PhoneNumber[];
      }
    > = {};

    managers.forEach((managerData) => {
      const { id, ...restManagerData } = managerData;

      if (!groupedEmployeeManagers[id]) {
        groupedEmployeeManagers[id] = {
          ...restManagerData,
          types: new Set(),
          phones: this.processEmployeeManagersPhones(managerData),
        };
      }
      groupedEmployeeManagers[id].types.add(restManagerData.type);
    });

    return Object.entries(groupedEmployeeManagers)
      .map(([id, { types, ...restManagerData }]) => ({
        id,
        types: Array.from(types),
        ...restManagerData,
      }))
      .sort(this.processEmployeeManagersSort);
  }

  processEmployeeManagersPhones({
    mobile_phone,
    personal_mobile_phone,
    work_phone,
    work_mobile_phone,
  }: EmployeeManager) {
    return [
      {
        label: t('user.profile.managers.workPhone'),
        number: work_phone && this.format.toPhone(work_phone),
      },
      {
        label: t('user.profile.managers.workMobilePhone'),
        number: work_mobile_phone && this.format.toPhone(work_mobile_phone),
      },
      {
        label: t('user.profile.managers.mobilePhone'),
        number: mobile_phone && this.format.toPhone(mobile_phone),
      },
      {
        label: t('user.profile.managers.personalMobilePhone'),
        number:
          personal_mobile_phone && this.format.toPhone(personal_mobile_phone),
      },
    ].filter((phone) => !!phone.number) as PhoneNumber[];
  }

  processEmployees(employee: ManagerSubstitute) {
    const { substitute, dates } = employee;

    return {
      id: substitute.id,
      name: substitute.name,
      personnelNumber: substitute.personnel_number,
      email: substitute.email,
      position: substitute.position,
      phone: substitute.phone,
      company: {
        id: substitute.company.id,
        name: substitute.company.name,
      },
      dates: dates || [],
    };
  }

  processManagerSubstitutesEventType(
    event_types: ManagerSubstitutesEventType[],
  ) {
    return event_types.map(({ substitutes, event_type }) => ({
      substitutes: substitutes.map((substitute) => ({
        ...this.processEmployees(substitute),
      })),
      eventType: {
        id: event_type.id,
        name: event_type.name,
      },
    }));
  }

  processSubstitutes(
    items: ManageableCompanyItem[],
  ): CompanyPositionSubstitutes[] {
    return items.map(
      ({ id, name, employee, substitutes, functional_manageable_units }) => ({
        company: { id, name },
        employee: {
          id: employee.id,
          personalNumber: employee.personnel_number,
          position: employee.position || '',
          name: employee.name,
          showPosition: items.filter((item) => item.id === id).length > 1,
        },
        eventTypeGroup: substitutes.map(
          ({ event_type, substitutes: eventTypeSubstitutes }) => ({
            eventType: {
              id: event_type.id,
              name: event_type.name,
            },
            substitutes: eventTypeSubstitutes
              .map(
                ({
                  substitute,
                  available_options: availableOptions,
                  dates,
                }) => ({
                  id: substitute.id,
                  name: substitute.name,
                  personalNumber: substitute.personnel_number,
                  position: substitute.position,
                  company: substitute.company.name,
                  unit_name: substitute.unit_name,
                  dates: dates || [],
                  availableOptions: availableOptions && [
                    ...availableOptions.filter(
                      (option) => option.scope === 'ALL',
                    ),
                    ...availableOptions.filter(
                      (option) => option.scope !== 'ALL',
                    ),
                  ],
                }),
              )
              .sort((a, b) => a.name.localeCompare(b.name)),
          }),
        ),
        manageableUnits: functional_manageable_units?.map((item) => ({
          unit: {
            id: item.unit.id,
            name: item.unit.name,
          },
          roleInUnit: {
            label: this.processRoleInUnit(item.role_in_unit),
            role: item.role_in_unit,
          },
          allSetting: item.event_type_settings
            ?.filter((settings) => settings.approve === 'always')
            ?.map((settings) => settings.event_type),
          directSetting: item.event_type_settings
            ?.filter((settings) => settings.approve === 'direct_only')
            ?.map((settings) => settings.event_type),
          neverSetting: item.event_type_settings
            ?.filter((settings) => settings.approve === 'never')
            ?.map((settings) => settings.event_type),
        })),
      }),
    );
  }

  processCompanyEmployeeEventTypesSubstitutes({
    employee,
    company,
    eventTypeGroup,
  }: CompanyPositionSubstitutes) {
    const groupedEventTypesBySubstitute: Record<
      string,
      Record<string, SubstituteType>
    > = {};

    eventTypeGroup.forEach(({ substitutes, eventType }) => {
      substitutes.forEach(({ id, dates }) => {
        groupedEventTypesBySubstitute[id] = {
          ...groupedEventTypesBySubstitute[id],
          [eventType.id]: !!dates?.length ? 'temporary' : 'permanent',
        };
      });
    });

    return { company, employee, groupedEventTypesBySubstitute };
  }

  processRoleInUnit(roleInUnit: 'manager' | 'assistant') {
    switch (roleInUnit) {
      case 'manager':
        return 'Руководитель';
      case 'assistant':
        return 'Ассистент';
      default:
        return '';
    }
  }

  processToken1CExpiresAtDate(expiresAt: string) {
    return this.format.toDate(expiresAt);
  }

  processSubstituteDates(dates?: DateRange[]) {
    if (!dates?.length) {
      return 'Постоянный';
    }

    const substituteDatesLabel =
      this.format.toDateRangeWithDaysBetweenInclusive(
        dates[0].from_date,
        dates[0].to_date,
      );

    if (dates.length > 1) {
      return `${substituteDatesLabel} и еще ${dates.length - 1}`;
    }

    return substituteDatesLabel;
  }

  processCertificateErrorTitle(
    status: ExtendedUser['certificate']['status'],
  ): string {
    switch (status) {
      case 'error':
        return 'Не удалось выпустить УНЭП';
      case 'release_paused':
        return 'Ручная проверка данных';
      default:
        return 'Не удалось выпустить УНЭП';
    }
  }

  processCertificateErrorDescriptions(code?: SmevErrorCode | null): string[] {
    switch (code) {
      case 'passportExpired':
        return [
          'Срок действия вашего паспорта истёк.',
          t('user.createCertificate.certificateError.passportExpiredHr'),
        ];
      case 'passportReplaced':
        return [
          'Вы получили новый паспорт, но в системе указан старый. Обратитесь в отдел кадров, чтобы обновить данные.',
        ];
      case 'passportIssuedWithViolation':
        return ['Ваш паспорт выдан с нарушением'];
      case 'passportIsSearched':
        return ['Ваш паспорт числится в розыске'];
      case 'passportDestroyed':
        return ['Ваш паспорт числится как изъятый или уничтоженный'];
      case 'passportOwnerDied':
        return ['Ваш паспорт отмечен МВД как недействительный'];
      case 'passportDefect':
        return [
          'Ваш паспорт отмечен МВД как технически бракованный, его использование запрещено',
        ];
      case 'passportLost':
        return ['Ваш паспорт числится как утраченный'];
      case 'passportCitizenshipStopped':
        return [
          'Ваш паспорт недействителен в связи с прекращением гражданства',
        ];
      case 'snilsNotMatchWithPfr':
        return [
          'По представленному номеру СНИЛС, ФИО и дате рождения нет данных в ПФР или данные не совпадают',
          'Обратитесь в отдел кадров для проверки правильности внесённых данных в кадровой системе (ФИО, дата рождения, номер СНИЛС) и дальнейших действий',
        ];
      case 'innNotMatchWithFns':
        return [
          'Паспортные данные, указанные в ФНС для этого ИНН, не совпадают с предоставленными',
          'Обратитесь в отдел кадров, чтобы проверить корректность данных и их совпадение с налоговой системой',
        ];
      case 'notValidSnilsChecksum':
        return [
          'Неверное контрольное число у номера СНИЛС',
          'Введённый номер СНИЛС некорректен',
          'Обратитесь в отдел кадров для проверки правильности внесённых данных в кадровой системе и дальнейших действий',
        ];
      case 'pfrNotAvailable':
        return [
          'Не удалось проверить документ в ПФР',
          'Обратитесь в отдел кадров для проверки правильности внесённых данных в кадровой системе (СНИЛС, ИНН, паспортные данные) и дальнейших действий',
        ];
      case 'fnsNotAvailable':
        return [
          'Не удалось проверить документ в ФНС',
          'Обратитесь в отдел кадров для проверки правильности внесённых данных в кадровой системе (СНИЛС, ИНН, паспортные данные) и дальнейших действий',
        ];
      case 'fmsNotAvailable':
        return [
          'Не удалось проверить документ в ФМС',
          'Обратитесь в отдел кадров для проверки правильности внесённых данных в кадровой системе (СНИЛС, ИНН, паспортные данные) и дальнейших действий',
        ];
      case 'organizationNotFoundInFns':
        return [
          'Организация не найдена в ФНС',
          'Обратитесь в отдел кадров для проверки правильности внесённых данных в кадровой системе и дальнейших действий',
        ];
      case 'unknown':
      case 'unknownConfirmationError':
        return [
          'Попробуйте выпустить УНЭП повторно.',
          'Если ошибка сохраняется, обратитесь в отдел кадров вашей компании',
        ];
      case 'userNotFoundInEsia':
        return ['Ваша подтверждённая учётная запись не найдена на Госуслугах'];
      case 'confirmationRejected':
        return [
          'Вы отклонили запрос на выпуск УНЭП.',
          'Для продолжения нажмите «Перевыпустить» и следуйте инструкциям на экране',
        ];
      case 'confirmationTimeout':
        return [
          'Время на подтверждение выпуска УНЭП истекло.',
          'Для продолжения нажмите «Перевыпустить» и следуйте инструкциям на экране',
        ];
      default:
        return [t('user.createCertificate.certificateError.defaultError')];
    }
  }

  processCertificateErrorInformations(
    code?: SmevErrorCode | null,
  ): { title: string; items: string[] }[] {
    switch (code) {
      case 'passportExpired':
        return [
          {
            title: 'Для уточнения статуса:',
            items: [
              'Подайте обращение на сайте МВД в разделе ГУВМ, следуя инструкциям на сайте',
              'Дождитесь письма от МВД о внесённых исправлениях',
              'После исправления сообщите в отдел кадров для перевыпуска УНЭП',
            ],
          },
          {
            title: 'Если срок действия паспорта истёк:',
            items: [
              'Замените паспорт в МВД',
              'После получения нового паспорта сообщите в отдел кадров для перевыпуска УНЭП',
            ],
          },
        ];
      case 'userNotFoundInEsia':
        return [
          {
            title: 'Возможные причины:',
            items: [
              'У вас отсутствует подтверждённая учётная запись на Госуслугах',
              'Данные вашей учётной записи на Госуслугах не совпадают с данными в системе учёта кадров вашей компании',
            ],
          },
          {
            title: 'Как исправить ошибку:',
            items: [
              'Проверьте, что ваша учётная запись на Госуслугах подтверждена и содержит актуальную информацию',
              'Обратитесь в отдел кадров вашей компании для проверки корректности ваших данных в системе учёта кадров',
              'Вернитесь в КЭДО и нажмите кнопку «Перевыпустить»',
            ],
          },
        ];
      case 'passportReplaced':
      case 'passportIssuedWithViolation':
      case 'passportIsSearched':
      case 'passportDestroyed':
      case 'passportOwnerDied':
      case 'passportDefect':
      case 'passportLost':
      case 'passportCitizenshipStopped':
        return [
          {
            title: 'Для уточнения статуса:',
            items: [
              'Подайте обращение на сайте МВД в разделе ГУВМ, следуя инструкциям на сайте',
              'Дождитесь письма от МВД о внесённых исправлениях',
              'После исправления сообщите в отдел кадров для перевыпуска УНЭП',
            ],
          },
        ];
      default:
        return [];
    }
  }

  shouldShowKnowledgeBaseLink(code?: SmevErrorCode | null): boolean {
    const knowledgeBaseCodes: SmevErrorCode[] = [
      'passportExpired',
      'passportReplaced',
      'passportIssuedWithViolation',
      'passportIsSearched',
      'passportDestroyed',
      'passportOwnerDied',
      'passportDefect',
      'passportLost',
      'passportCitizenshipStopped',
      'snilsNotMatchWithPfr',
      'innNotMatchWithFns',
      'notValidSnilsChecksum',
      'pfrNotAvailable',
      'fnsNotAvailable',
      'fmsNotAvailable',
      'organizationNotFoundInFns',
    ];

    return code ? knowledgeBaseCodes.includes(code) : false;
  }
}
